/**
 * PIP-176 / PIP-178 CLT operator-list rings, as GeoJSON `[lon, lat]` closed
 * polygons. Re-copied from `packages/db-core/prisma/seeds/neighborhoods/clt.json`
 * (GEO-3 added montford + villa-heights and reshaped south-end / loso).
 * Do not invent vertices — re-copy from the seed when the operator list changes.
 *
 * These are the same public Charlotte neighborhood approximations the seed
 * writes to PostGIS. Discovery v2 hoodmarks project them locally (WEB-787)
 * so both hosts can draw a real footprint without waiting on API-1657.
 */
import type { CltNeighborhoodSlug } from '@/lib/neighborhoods/clt-slugs';
import { CLT_NEIGHBORHOOD_SLUGS } from '@/lib/neighborhoods/clt-slugs';

/** Closed GeoJSON ring: first vertex equals last. */
export type LonLatRing = readonly (readonly [number, number])[];

export const CLT_SEED_RINGS: Readonly<Record<CltNeighborhoodSlug, LonLatRing>> = {
  // COR-78 — the I-277 / I-77 freeway loop, plus the Hamilton St arm that
  // carries the AvidXchange Music Factory.
  uptown: [
    [-80.853, 35.237],
    [-80.849, 35.241],
    [-80.842, 35.24],
    [-80.844, 35.236],
    [-80.836, 35.233],
    [-80.8285, 35.2305],
    [-80.832, 35.2205],
    [-80.84, 35.2192],
    [-80.848, 35.221],
    [-80.856, 35.2245],
    [-80.853, 35.237],
  ],
  // GEO-3 — east edge stops near South Blvd so East Blvd & Cleveland stays Dilworth.
  'south-end': [
    [-80.85121, 35.22094],
    [-80.85062, 35.22042],
    [-80.84803, 35.2175],
    [-80.84793, 35.21526],
    [-80.84899, 35.21461],
    [-80.85039, 35.21373],
    [-80.85138, 35.21313],
    [-80.85208, 35.21269],
    [-80.85328, 35.21194],
    [-80.85432, 35.211],
    [-80.8554, 35.21002],
    [-80.85648, 35.20905],
    [-80.85752, 35.20811],
    [-80.85901, 35.20677],
    [-80.85985, 35.20601],
    [-80.86265, 35.20405],
    [-80.86447, 35.20198],
    [-80.865, 35.197],
    [-80.86655, 35.21115],
    [-80.86005, 35.21467],
    [-80.85831, 35.21533],
    [-80.85478, 35.21748],
    [-80.85121, 35.22094],
  ],
  // COR-78 — the Central Ave corridor (Central is the spine, not the edge)
  // from Hawthorne Ln to Briar Creek, bounded south by E Independence Blvd.
  'plaza-midwood': [
    [-80.8185, 35.2213],
    [-80.8125, 35.2255],
    [-80.81, 35.235],
    [-80.803, 35.234],
    [-80.788, 35.22],
    [-80.7975, 35.2145],
    [-80.821, 35.215],
    [-80.8185, 35.2213],
  ],
  noda: [
    [-80.806, 35.241],
    [-80.814, 35.246],
    [-80.801, 35.255],
    [-80.794, 35.249],
    [-80.806, 35.241],
  ],
  // COR-78 — Elizabeth Ave / E 7th St / Randolph Rd, N Kings Dr to Pecan Ave.
  elizabeth: [
    [-80.829, 35.2225],
    [-80.821, 35.2222],
    [-80.8175, 35.2172],
    [-80.816, 35.212],
    [-80.8246, 35.2108],
    [-80.8321, 35.2181],
    [-80.829, 35.2225],
  ],
  dilworth: [
    [-80.846, 35.215],
    [-80.835, 35.209],
    [-80.849, 35.195],
    [-80.858, 35.203],
    [-80.846, 35.215],
  ],
  'south-park': [
    [-80.835, 35.158],
    [-80.815, 35.15],
    [-80.83, 35.135],
    [-80.852, 35.145],
    [-80.835, 35.158],
  ],
  // GEO-3 — northwest edge reaches I-77 & Clanton so the Yancey Rd breweries sit inside.
  loso: [
    [-80.865, 35.197],
    [-80.858, 35.19],
    [-80.875, 35.174],
    [-80.884, 35.182],
    [-80.886, 35.18476],
    [-80.87729, 35.19589],
    [-80.865, 35.197],
  ],
  // COR-99 — Freedom / Morehead corridor; no longer covers Wesley Heights.
  freemorewest: [
    [-80.856, 35.2245],
    [-80.867, 35.22896],
    [-80.87038, 35.23117],
    [-80.89111, 35.22431],
    [-80.856, 35.2245],
  ],
  mora: [
    [-80.771, 35.189],
    [-80.748, 35.178],
    [-80.763, 35.161],
    [-80.787, 35.171],
    [-80.771, 35.189],
  ],
  'camp-north-end': [
    [-80.831, 35.253],
    [-80.819, 35.247],
    [-80.826, 35.239],
    [-80.837, 35.244],
    [-80.831, 35.253],
  ],
  'myers-park': [
    [-80.83, 35.205],
    [-80.811, 35.196],
    [-80.834, 35.165],
    [-80.846, 35.188],
    [-80.83, 35.205],
  ],
  ballantyne: [
    [-80.856, 35.064],
    [-80.821, 35.055],
    [-80.84, 35.018],
    [-80.871, 35.032],
    [-80.856, 35.064],
  ],
  'university-city': [
    [-80.765, 35.312],
    [-80.74, 35.328],
    [-80.708, 35.305],
    [-80.738, 35.286],
    [-80.765, 35.312],
  ],
  // COR-99 — south edge is Beatties Ford × W Trade, not inside Wesley Heights.
  'historic-west-end': [
    [-80.853, 35.237],
    [-80.85611, 35.25283],
    [-80.864, 35.25],
    [-80.88, 35.242],
    [-80.85866, 35.2417],
    [-80.853, 35.237],
  ],
  'optimist-park': [
    [-80.833, 35.235],
    [-80.824, 35.242],
    [-80.819, 35.238],
    [-80.828, 35.23],
    [-80.833, 35.235],
  ],
  cotswold: [
    [-80.803, 35.188],
    [-80.781, 35.183],
    [-80.793, 35.166],
    [-80.814, 35.174],
    [-80.803, 35.188],
  ],
  // COR-99 — NRHP / local HDC named-street ring.
  'wesley-heights': [
    [-80.86383, 35.22901],
    [-80.86535, 35.22918],
    [-80.86701, 35.22938],
    [-80.86821, 35.2302],
    [-80.86801, 35.23191],
    [-80.86441, 35.23555],
    [-80.86465, 35.23682],
    [-80.86148, 35.23692],
    [-80.85926, 35.237],
    [-80.85816, 35.23612],
    [-80.86383, 35.22901],
  ],
  // COR-99 — Wilmore HDC west of S Tryon.
  wilmore: [
    [-80.8639, 35.22317],
    [-80.85622, 35.21611],
    [-80.85831, 35.21533],
    [-80.86005, 35.21467],
    [-80.86655, 35.21115],
    [-80.87367, 35.21181],
    [-80.87161, 35.21698],
    [-80.8639, 35.22317],
  ],
  cherry: [
    [-80.833, 35.213],
    [-80.827, 35.211],
    [-80.83, 35.204],
    [-80.837, 35.207],
    [-80.833, 35.213],
  ],
  sedgefield: [
    [-80.855, 35.201],
    [-80.844, 35.194],
    [-80.855, 35.185],
    [-80.864, 35.192],
    [-80.855, 35.201],
  ],
  belmont: [
    [-80.828, 35.233],
    [-80.821, 35.237],
    [-80.814, 35.224],
    [-80.823, 35.221],
    [-80.828, 35.233],
  ],
  'steele-creek': [
    [-80.975, 35.165],
    [-80.932, 35.12],
    [-80.985, 35.055],
    [-81.035, 35.105],
    [-80.975, 35.165],
  ],
  // GEO-3 — Park Rd / Woodlawn / Selwyn / Mockingbird.
  montford: [
    [-80.85123, 35.17208],
    [-80.8412, 35.17097],
    [-80.84218, 35.16725],
    [-80.85045, 35.16734],
    [-80.85123, 35.17208],
  ],
  // GEO-3 — between NoDa, Optimist Park, and Belmont.
  'villa-heights': [
    [-80.82289, 35.23647],
    [-80.80798, 35.23282],
    [-80.79958, 35.23809],
    [-80.81216, 35.2419],
    [-80.81749, 35.24118],
    [-80.82289, 35.23647],
  ],
};

const SLUG_SET: ReadonlySet<string> = new Set(CLT_NEIGHBORHOOD_SLUGS);

export function getCltSeedRing(slug: string): LonLatRing | null {
  return SLUG_SET.has(slug) ? CLT_SEED_RINGS[slug as CltNeighborhoodSlug] : null;
}
